/**
 * ProjectRouter
 * Wrapper component that syncs URL shortId to ProjectContext.
 * Used as parent route for all /p/:shortId/* routes.
 * Always renders Outlet — ProjectGuard handles the loading boundary.
 */

import { useLayoutEffect } from 'react';
import { Outlet, useParams, useNavigate } from 'react-router-dom';
import { Sentry } from '@martha-v5/sentry/browser';
import { useProject } from '../../contexts/ProjectContext';
import { SprintProvider } from '../../contexts/SprintContext';
import { ProjectGuard } from './ProjectGuard';

interface ProjectRouterProps {
  /**
   * Fallback route when project is not found
   * Defaults to '/'
   */
  notFoundRedirect?: string;
}

export function ProjectRouter({ notFoundRedirect = '/' }: ProjectRouterProps) {
  const { shortId: urlShortId } = useParams<{ shortId: string }>();
  const navigate = useNavigate();
  const {
    shortId: contextShortId,
    setProjectByShortId,
    isLoading,
  } = useProject();

  // Sync URL shortId to context before paint
  useLayoutEffect(() => {
    if (isLoading || !urlShortId) return;

    if (urlShortId !== contextShortId) {
      const success = setProjectByShortId(urlShortId);
      if (!success) {
        console.warn(`Project with shortId "${urlShortId}" not found, redirecting to ${notFoundRedirect}`);
        Sentry.addBreadcrumb({
          category: 'navigation',
          message: `Project "${urlShortId}" not found — redirecting`,
          level: 'warning',
        });
        navigate(notFoundRedirect, { replace: true });
      }
    }
  }, [urlShortId, contextShortId, setProjectByShortId, isLoading, navigate, notFoundRedirect]);

  // Always render — ProjectGuard shows content-area spinner while loading.
  return <SprintProvider><ProjectGuard><Outlet /></ProjectGuard></SprintProvider>;
}

export default ProjectRouter;
